import React, { useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import Button from './Button'
import Input from './Input'
import '../styles/token-popup.css'

// Состояния: idle → loading → view → edit → saving → (view + banner) | (edit + error)
export default function TokenPopup ({ open, onClose, onSaved, userId }) {
  const [token, setToken] = useState(null)
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState('view') // 'view' | 'edit'
  const [input, setInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [banner, setBanner] = useState(false)
  const [error, setError] = useState(null)
  const ref = useRef(null)
  const previousFocus = useRef(null)

  useEffect(() => {
    if (!open) return
    setMode('view')
    setBanner(false)
    setError(null)
    setLoading(true)
    api.getFintabloToken(userId)
      .then(data => setToken(data.fintablo_token))
      .catch(() => setToken(null))
      .finally(() => setLoading(false))
  }, [open, userId])

  // Закрытие по клику вне попапа
  useEffect(() => {
    if (!open) return
    function handleMouseDown (e) {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open, onClose])

  // Диалог получает фокус, удерживает keyboard navigation внутри и возвращает
  // фокус на кнопку, которая его открыла.
  useEffect(() => {
    if (!open) return
    previousFocus.current = document.activeElement
    ref.current?.focus()

    function handleKey (e) {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab' || !ref.current) return

      const focusable = Array.from(ref.current.querySelectorAll(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'
      ))
      if (focusable.length === 0) {
        e.preventDefault()
        ref.current.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('keydown', handleKey)
      previousFocus.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  function handleReplace () {
    setInput(token || '')
    setError(null)
    setMode('edit')
  }

  function handleCancel () {
    setError(null)
    setMode('view')
  }

  async function handleSave () {
    setSaving(true)
    setError(null)
    try {
      await api.saveFintabloToken(userId, input)
      setToken(input)
      onSaved?.(Boolean(input.trim()))
      setMode('view')
      setBanner(true)
      setTimeout(() => setBanner(false), 3000)
    } catch (err) {
      setError(err.message || 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='token-popup__overlay'>
      <section
        aria-labelledby='fintablo-token-title'
        aria-modal='true'
        className='token-popup'
        ref={ref}
        role='dialog'
        tabIndex='-1'
      >
        <h2 className='token-popup__title' id='fintablo-token-title'>Токен Финтабло</h2>

        {banner && <div className='token-popup__banner' role='status'>Токен обновлён</div>}

        {loading
          ? <div className='token-popup__loading'>Загрузка…</div>
          : mode === 'view'
            ? (
              <div className='token-popup__content'>
                <div className='token-popup__token'>{token || <span className='token-popup__empty'>Токен не задан</span>}</div>
                <Button variant='stroke' onClick={handleReplace}>
                  {token ? 'Заменить' : 'Задать'}
                </Button>
              </div>
              )
            : (
              <div className='token-popup__content'>
                <label className='ui-sr-only' htmlFor='fintablo-token-input'>Новый токен Финтабло</label>
                <Input
                  autoFocus
                  className='token-popup__input'
                  id='fintablo-token-input'
                  type='text'
                  value={input}
                  onChange={e => setInput(e.target.value)}
                />
                {error && <div className='token-popup__error' role='alert'>{error}</div>}
                <div className='token-popup__actions'>
                  <Button loading={saving} onClick={handleSave}>Сохранить</Button>
                  <Button variant='ghost' onClick={handleCancel} disabled={saving}>Отмена</Button>
                </div>
              </div>
              )}
      </section>
    </div>
  )
}
